import { useState } from "react";
import { api } from "../../lib/api";
import { useLanguage } from "../../context/LanguageProvider";
import { pick, UI_STRINGS } from "../../lib/i18n";
import { Send, CheckCircle2 } from "lucide-react";

/**
 * Quick contact form for the Home page. source="home" on the backend, no
 * consent required, honeypot field `website` must remain empty.
 */
export function QuickContactForm() {
    const { lang } = useLanguage();
    const [form, setForm] = useState({
        email: "",
        subject: "",
        message: "",
        website: "", // honeypot
    });
    const [state, setState] = useState({
        status: "idle", // idle | submitting | success | error
        error: "",
    });

    const canSubmit =
        form.email.trim() && form.message.trim() && state.status !== "submitting";

    const onChange = (name) => (e) =>
        setForm((f) => ({ ...f, [name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setState({ status: "submitting", error: "" });
        try {
            await api.post("/contact", {
                source: "home",
                email: form.email.trim(),
                subject: form.subject.trim() || undefined,
                message: form.message.trim(),
                website: form.website, // still send honeypot, backend silently drops
            });
            setState({ status: "success", error: "" });
            setForm({ email: "", subject: "", message: "", website: "" });
        } catch (err) {
            setState({
                status: "error",
                error: err?.response?.data?.detail?.message || err.message || "",
            });
        }
    };

    if (state.status === "success") {
        return (
            <div
                data-testid="quick-form-success"
                className="border border-gold/60 bg-ink-surface p-6 flex items-start gap-4"
            >
                <CheckCircle2
                    className="text-gold shrink-0 mt-1"
                    size={22}
                    strokeWidth={1.5}
                />
                <div>
                    <div className="font-serif text-xl text-neutral-50 mb-1">
                        {pick(
                            {
                                fr: "Message reçu",
                                en: "Message received",
                            },
                            lang,
                        )}
                    </div>
                    <div className="text-sm text-neutral-300 leading-relaxed">
                        {pick(UI_STRINGS.form.success, lang)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            data-testid="quick-contact-form"
            onSubmit={onSubmit}
            className="grid grid-cols-1 gap-5"
            noValidate
        >
            {/* Honeypot — invisible to real users, ignored on the backend when empty */}
            <div
                className="absolute -left-[9999px] w-px h-px overflow-hidden"
                aria-hidden="true"
            >
                <label>
                    Website
                    <input
                        tabIndex={-1}
                        autoComplete="off"
                        type="text"
                        name="website"
                        value={form.website}
                        onChange={onChange("website")}
                    />
                </label>
            </div>

            <label className="block">
                <span className="eyebrow block mb-1">
                    {pick(UI_STRINGS.form.email, lang)}
                </span>
                <input
                    type="email"
                    required
                    className="field-line"
                    placeholder="votre@courriel.com"
                    value={form.email}
                    onChange={onChange("email")}
                    data-testid="quick-form-email"
                />
            </label>

            <label className="block">
                <span className="eyebrow block mb-1">
                    {pick(UI_STRINGS.form.subject, lang)}
                </span>
                <input
                    type="text"
                    className="field-line"
                    placeholder={pick(
                        {
                            fr: "Objet de votre demande",
                            en: "Subject of your request",
                        },
                        lang,
                    )}
                    value={form.subject}
                    onChange={onChange("subject")}
                    data-testid="quick-form-subject"
                />
            </label>

            <label className="block">
                <span className="eyebrow block mb-1">
                    {pick(UI_STRINGS.form.message, lang)}
                </span>
                <textarea
                    rows={4}
                    required
                    className="field-line resize-none"
                    placeholder={pick(
                        {
                            fr: "Exposez brièvement votre situation…",
                            en: "Briefly outline your situation…",
                        },
                        lang,
                    )}
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid="quick-form-message"
                />
            </label>

            {state.status === "error" && (
                <div
                    data-testid="quick-form-error"
                    className="text-sm text-red-300 border border-red-900 bg-red-950/40 px-3 py-2"
                >
                    {pick(UI_STRINGS.form.error, lang)}
                </div>
            )}

            <button
                type="submit"
                disabled={!canSubmit}
                className="btn-gold justify-self-start"
                data-testid="quick-form-submit"
            >
                {state.status === "submitting"
                    ? pick(UI_STRINGS.cta.sending, lang)
                    : pick(UI_STRINGS.cta.send, lang)}
                {state.status !== "submitting" && <Send size={16} />}
            </button>
        </form>
    );
}
